import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, FileText, MapPin, Pencil, Phone, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, fileUrl, formatErr } from "@/lib/api";
import { canDelete, canSeeOwner, telLink } from "@/lib/constants";
import { areaText, fmtDateOnly, fmtDateTime, inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useUserMap, useBranding } from "@/hooks/useData";
import { InfoRow, Spinner, StatusBadge, UserAvatar } from "@/components/common";
import { ConfirmDialog } from "@/components/Dialogs";
import MatchList from "@/components/MatchList";
import { PropertyThumb, priceLabel } from "@/components/properties/PropertyCard";
import { shareProperty } from "@/components/properties/share";
import { cn } from "@/lib/utils";

function Gallery({ p }) {
  const [i, setI] = useState(0);
  if (!p.photos?.length) return <div className="overflow-hidden rounded-2xl border"><PropertyThumb p={p} className="h-64" /></div>;
  return (
    <div className="space-y-2" data-testid="property-gallery">
      <div className="overflow-hidden rounded-2xl border bg-black">
        <img src={fileUrl(p.photos[i].path)} alt="" className="h-64 w-full object-contain sm:h-96" />
      </div>
      {p.photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none">
          {p.photos.map((ph, j) => (
            <button key={ph.path} onClick={() => setI(j)} data-testid={`gallery-thumb-${j}`} className={cn("h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2", j === i ? "border-primary" : "border-transparent opacity-70")}>
              <img src={fileUrl(ph.path)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const userMap = useUserMap();
  const [del, setDel] = useState(false);
  const { data: brand } = useBranding();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["property", id], queryFn: async () => (await api.get(`/properties/${id}`)).data });

  const remove = async () => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      qc.invalidateQueries({ queryKey: ["properties"] });
      nav("/properties");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (isLoading) return <Spinner />;
  if (error) return <p className="text-destructive">{formatErr(error)}</p>;
  const owner = userMap[p.assigned_to];
  const creator = userMap[p.created_by];
  const side = p.listing_type === "Purchase requirement" ? "requirements" : "inventory";
  return (
    <div className="space-y-6">
      <Link to={`/properties?side=${side}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground" data-testid="property-back-link"><ArrowLeft className="h-4 w-4" />Properties</Link>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Gallery p={p} />
        <div className="space-y-4 rounded-2xl border bg-card p-5 fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold">{p.listing_type}</span>
            <StatusBadge status={p.status} />
            {p.negotiable && <span className="text-xs font-semibold text-emerald-600">Negotiable</span>}
          </div>
          <div>
            <p className="num text-3xl font-semibold" data-testid="property-detail-price">{priceLabel(p)}</p>
            <p className="num text-xs text-muted-foreground">{inr(p.price)}{p.price_per_sqft ? ` · ${inr(p.price_per_sqft)}/sq ft` : ""}</p>
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight" data-testid="property-detail-title">{p.property_type}{p.bhk ? ` · ${p.bhk}` : ""}</h1>
            <p className="text-sm text-muted-foreground">{p.project}</p>
            <p className="mt-1 flex items-center gap-1 text-sm"><MapPin className="h-4 w-4 text-muted-foreground" />{[p.address || p.sector, p.city].filter(Boolean).join(", ")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => shareProperty(p, brand?.company_name)} className="bg-[#25D366] text-white hover:bg-[#1ebe5a]" data-testid="property-detail-share-button"><Share2 className="mr-1.5 h-4 w-4" />Share on WhatsApp</Button>
            <Button variant="outline" asChild data-testid="property-edit-button"><Link to={`/properties/${id}/edit`}><Pencil className="mr-1.5 h-4 w-4" />Edit</Link></Button>
            {p.maps_link && <Button variant="outline" asChild data-testid="property-map-button"><a href={p.maps_link} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" />Map</a></Button>}
            {canDelete(user) && <Button variant="outline" className="text-destructive" onClick={() => setDel(true)} data-testid="property-delete-button"><Trash2 className="h-4 w-4" /></Button>}
          </div>
          {canSeeOwner(user) && (
            <div className="rounded-xl bg-secondary/60 p-3" data-testid="property-owner-info">
              <p className="label-xs">Owner / source</p>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm font-semibold">{p.owner_name || "—"}{p.owner_phone && <span className="num ml-2 font-normal text-muted-foreground">{p.owner_phone}</span>}</span>
                {p.owner_phone && <a href={telLink(p.owner_phone)} className="rounded-full border bg-card p-2 text-sky-600" data-testid="property-owner-call"><Phone className="h-4 w-4" /></a>}
              </div>
            </div>
          )}
        </div>
      </div>
      <section className="rounded-2xl border bg-card p-5">
        <h3 className="mb-4 font-display font-bold">Details</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <InfoRow label="Area" value={areaText(p.area, p.area_unit)} />
          <InfoRow label="Floor" value={p.floor} />
          <InfoRow label="Facing" value={p.facing} />
          <InfoRow label="Ownership" value={p.ownership} />
          <InfoRow label="Possession" value={p.possession ? `${p.possession}${p.possession_date ? ` · ${fmtDateOnly(p.possession_date)}` : ""}` : "—"} />
          <InfoRow label="Sector" value={p.sector} />
          <InfoRow label="City" value={p.city} />
          <InfoRow label="Co-broker involved" value={p.co_broker ? "Yes" : "No"} />
          <InfoRow label="Assigned to" value={owner ? <span className="inline-flex items-center gap-2"><UserAvatar user={owner} />{owner.name}</span> : "Unassigned"} />
          <InfoRow label="Created by" value={creator?.name} />
          <InfoRow label="Created on" value={fmtDateTime(p.created_at)} />
        </div>
        {p.notes && <p className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary/60 p-3 text-sm">{p.notes}</p>}
      </section>
      {p.documents?.length > 0 && (
        <section className="rounded-2xl border bg-card p-5">
          <h3 className="mb-4 font-display font-bold">Documents</h3>
          <div className="flex flex-wrap gap-3">
            {p.documents.map((d, i) => (
              <a key={d.path} href={fileUrl(d.path)} target="_blank" rel="noreferrer" data-testid={`property-doc-${i}`} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-secondary">
                <FileText className="h-4 w-4 text-primary" />{d.name}
              </a>
            ))}
          </div>
        </section>
      )}
      <section className="rounded-2xl border bg-card p-5" data-testid="property-matches-section">
        <h3 className="mb-4 font-display font-bold">Matches</h3>
        <MatchList params={{ property_id: id }} testid="property-matches" />
      </section>
      <ConfirmDialog open={del} onOpenChange={setDel} title="Delete this property?" text="This cannot be undone." onConfirm={remove} testid="property-delete-dialog" />
    </div>
  );
}
